import { createApiClient, createRawApiClient } from './client';
import type { Login } from './types';

/**
 * Social login (Wave 1, appcore `social-login-w1-contract.md`). Thin typed
 * wrappers over the façade's `/me/provision` and `/me/logins` passthroughs,
 * mirroring `api/mfa.ts`'s conventions: unwrap `.data` once, here, rather than
 * at each call site.
 */

// ── Provisioning (session-less federated signup) ──

/**
 * `POST /me/provision` — mints a USR + IDLINK for a caller whose access token
 * is valid (they completed Cognito sign-in) but who has no appcore identity
 * yet: a brand-new Google-first signup. Deliberately uses
 * {@link createRawApiClient}, NOT the intercepted client: this call is what
 * ESTABLISHES the caller's identity in the first place, so it can never itself
 * be gated behind a step-up ceremony (nor would the workspace-scoped
 * `X-Workspace-Id` header mean anything yet) — same reasoning as the step-up
 * ceremony's own calls in api/mfa.ts.
 */
export function provisionAccount(token: string, input: { idToken: string }): Promise<{ userId: string }> {
  return createRawApiClient(token)
    .post<{ data: { userId: string } }>('/me/provision', input)
    .then(r => r.data);
}

// ── Login management (the Logins section of Settings -> Security, W1.6) ──

/** Shared React Query key for the caller's login list (mirrors FACTORS_QUERY_KEY). */
export const LOGINS_QUERY_KEY = ['me', 'logins'] as const;

export function listLogins(token: string): Promise<Login[]> {
  return createApiClient(token).get<{ data: { logins: Login[] } }>('/me/logins').then(r => r.data.logins);
}

/**
 * `POST /me/logins` — the link ceremony (W1.5). Requires a LIVE reveal-tier
 * step-up ceremony to have just completed (the caller does that itself,
 * inline, before calling this — see LinkLoginModal) and deliberately uses
 * {@link createRawApiClient}: the link modal needs to catch a
 * `403 STEP_UP_REQUIRED` (its verification expired between moments) itself,
 * with its own "Verify again" copy and control flow, rather than have the
 * intercepted client silently reopen the GLOBAL step-up modal on top of it.
 */
export function addLogin(token: string, input: { providerToken: string }): Promise<{ linkId: string }> {
  return createRawApiClient(token)
    .post<{ data: { linkId: string } }>('/me/logins', input)
    .then(r => r.data);
}

/** `DELETE /me/logins/{linkId}` — refuses the last method (400/422 LAST_LOGIN_METHOD). */
export function removeLogin(token: string, linkId: string): Promise<{ deleted: true }> {
  return createApiClient(token)
    .delete<{ data: { deleted: true } }>(`/me/logins/${encodeURIComponent(linkId)}`)
    .then(r => r.data);
}
